import React, { useRef, useMemo, useState, forwardRef } from 'react'
import { useFrame } from '@react-three/fiber'
import { useGLTF, useTexture } from '@react-three/drei'
import * as THREE from 'three'

/**
 * Phone3DModel Component
 * 
 * Renders a 3D phone model with a customizable screen texture.
 * The phone model should have a mesh named "screen" that this component will target.
 * 
 * @param {string} screenTexture - Path to the texture image for the phone screen
 * @param {object} position - Position of the phone in 3D space [x, y, z]
 * @param {object} rotation - Rotation of the phone in 3D space [x, y, z]
 * @param {number} scale - Scale of the phone model
 * @param {function} onClick - Click handler for the phone
 * @param {function} onPointerOver - Hover enter handler
 * @param {function} onPointerOut - Hover exit handler
 * @param {React.Ref} ref - Forwarded ref to access the phone group
 */
export const Phone3DModel = forwardRef(({ 
  screenTexture, 
  position = [0, 0, 0], 
  rotation = [0, 0, 0], 
  scale = 100, // MUCH larger default scale - the GLB model is tiny
  onClick,
  onPointerOver,
  onPointerOut,
  children // Accept children to render inside the phone group
}, ref) => {
  const groupRef = useRef()
  
  // Expose the ref to parent
  React.useImperativeHandle(ref, () => groupRef.current)
  
  // Load the phone model
  const { nodes, materials } = useGLTF('/models/model-prop-phone-1.0.glb')
  
  // Debug: Log all node names
  useMemo(() => {
    console.log('Phone Model Nodes:', Object.keys(nodes).map(key => ({
      name: nodes[key].name,
      isMesh: nodes[key].isMesh
    })))
  }, [nodes])
  
  // Load the screen texture
  const texture = useTexture(screenTexture)
  
  // Configure texture to prevent stretching and ensure proper display
  useMemo(() => {
    if (texture) {
      texture.flipY = false // GLB models typically don't need flipY
      texture.colorSpace = THREE.SRGBColorSpace
      texture.minFilter = THREE.LinearFilter
      texture.magFilter = THREE.LinearFilter
    }
  }, [texture])
  
  // Create a custom material for the screen with the texture
  const screenMaterial = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      map: texture,
      emissive: new THREE.Color(0xffffff),
      emissiveIntensity: 0.4, // Screen glow
      emissiveMap: texture,
      roughness: 0.1, // Very smooth/glossy screen
      metalness: 0.0,
      toneMapped: false // Prevent tone mapping for accurate colors
    })
  }, [texture])
  
  return (
    <group 
      ref={groupRef} 
      position={position} 
      rotation={rotation}
      scale={scale}
      onClick={onClick}
    >
      {/* Render all meshes from the phone model */}
      {Object.keys(nodes).map((key) => {
        const node = nodes[key]
        
        // Skip if not a mesh
        if (!node.isMesh) return null
        
        // Check if this is the screen mesh
        const isScreen = node.name.toLowerCase().includes('screen')
        
        return (
          <mesh
            key={key}
            name={node.name} // Preserve the name so we can find it later
            geometry={node.geometry}
            material={isScreen ? screenMaterial : node.material}
            position={node.position}
            rotation={node.rotation}
            scale={node.scale}
            onClick={onClick}
            onPointerOver={onPointerOver}
            onPointerOut={onPointerOut}
            castShadow
            receiveShadow
          />
        )
      })}
      
      {/* Render children (like annotations) inside the phone group */}
      {children}
    </group>
  )
})

// Preload the phone model
useGLTF.preload('/models/model-prop-phone-1.0.glb')
